import React from 'react';
import { FadeIn, StaggerContainer, StaggerItem } from './FadeIn';
type EntryType = 'education' | 'work' | 'certification';
const experiences: {
  year: string;
  role: string;
  company: string;
  description: string;
  type: EntryType;
}[] = [{
  year: '2026 — Present',
  role: 'Paralegal Training Program',
  company: 'UCLA Extension',
  description: 'Pursuing certification covering civil litigation, contract law, legal research and writing, and professional ethics. Building the foundation for a career in the legal field.',
  type: 'education'
}, {
  year: '2025',
  role: 'Google Certificate in Data Analytics',
  company: 'Google / Coursera',
  description: 'Completed Google\u2019s professional certificate in data analytics, covering SQL, R, spreadsheets, and data visualization.',
  type: 'certification'
}, {
  year: '2025',
  role: 'B.A. in Economics',
  company: 'San Jose State University',
  description: 'Graduated with a Bachelor of Arts in Economics. Coursework sharpened my analytical thinking, research skills, and understanding of how systems and incentives shape decisions.',
  type: 'education'
}, {
  year: '2024 — 2025',
  role: 'Data Analyst',
  company: '77Labs',
  description: 'Worked as a data analyst for a year, translating raw data into clear insights for the team. Strengthened my eye for detail and my ability to work through complex problems methodically.',
  type: 'work'
}, {
  year: 'Mar 2022 — Jul 2023',
  role: 'Golf Operations Associate',
  company: 'Menlo Country Club',
  description: 'Delivered consistent, high-quality service to members and guests. Collaborated closely with coworkers to keep operations running smoothly and resolved member needs quickly.',
  type: 'work'
}, {
  year: 'Jul 2020 — Feb 2022',
  role: 'Outside Services Attendant',
  company: 'San Jose Country Club',
  description: 'Managed multiple tasks across a busy operation while keeping service efficient and courteous. Coordinated projects with the team to ensure everything was ready on time.',
  type: 'work'
}, {
  year: 'Jan 2019 — Jun 2020',
  role: 'Hospitality Team Member',
  company: 'The Source',
  description: 'Worked across both front and back of house at a busy cafe \u2014 prepping food to spec, serving customers, keeping the space clean, and stepping in wherever the team needed a hand.',
  type: 'work'
}];
const typeLabels: Record<EntryType, string> = {
  education: 'Education',
  work: 'Experience',
  certification: 'Certification'
};
export function Experience() {
  return <section className="py-20 md:py-32 px-6" data-id="element-30" data-mp="27qp5bb3vjnd9">
      <div className="max-w-4xl mx-auto" data-id="element-31" data-mp="23m7ljbxila2h">
        <FadeIn className="text-center mb-12 md:mb-20" data-id="element-32" data-mp="23pbvyr1kp21l">
          <h2 className="font-playfair text-3xl md:text-5xl text-charcoal mb-3 md:mb-4" data-id="element-33" data-mp="waqmifiw9myu">
            The Path So Far
          </h2>
          <p className="text-sage text-base md:text-lg italic" data-id="element-34" data-mp="s6sroh0lqybl">
            School, work, and a few stops in between.
          </p>
        </FadeIn>

        <div className="relative" data-id="element-35" data-mp="37wj0lpa375yh">
          {/* Center Line (Desktop) / Left Line (Mobile) */}
          <div className="absolute left-[15px] md:left-1/2 top-0 bottom-0 w-px bg-amber/20 md:-translate-x-1/2" data-id="element-36" data-mp="3cvlx2lc043uu" />

          <StaggerContainer className="space-y-12 md:space-y-24" data-id="element-37" data-mp="3hnvv6on9sa87">
            {experiences.map((exp, index) => {
            const isEven = index % 2 === 0;
            return <StaggerItem key={index} className="relative flex flex-col md:flex-row items-start md:items-center" data-id="element-38" data-mp="3rkic52ch6h4y" data-mpi={index}>
                  {/* Timeline Dot */}
                  <div className={`absolute left-[11px] md:left-1/2 w-[9px] h-[9px] rounded-full md:-translate-x-1/2 mt-2 md:mt-0 z-10 ring-4 ring-cream ${exp.type === 'work' ? 'bg-amber' : 'bg-sage'}`} data-id="element-39" data-mp="4f9abn1dtsfl" />

                  {/* Content Container */}
                  <div className={`w-full pl-12 md:pl-0 md:w-1/2 flex ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16 md:ml-auto'}`} data-id="element-40" data-mp="1awz8f4wrzuea">
                    <div className={`flex flex-col ${isEven ? 'md:text-right' : 'md:text-left'}`} data-id="element-41" data-mp="1kw835zzgtd1p">
                      <span className={`text-[10px] font-semibold tracking-[0.2em] uppercase mb-2 ${exp.type === 'work' ? 'text-amber' : 'text-sage'}`} data-id="element-42" data-mp="1fx4v06o6uqpi">
                        {typeLabels[exp.type]}
                      </span>
                      <span className="text-sm font-medium text-charcoal/60 mb-2 tracking-wider" data-id="element-43" data-mp="11xvqw5d9bn1w">
                        {exp.year}
                      </span>
                      <h3 className="font-playfair text-xl md:text-2xl text-charcoal mb-1" data-id="element-44" data-mp="9gje932ni3y9">
                        {exp.role}
                      </h3>
                      <span className="text-sage mb-3 md:mb-4 block italic text-sm md:text-base" data-id="element-45" data-mp="4gx26wyk6tr1">
                        {exp.company}
                      </span>
                      <p className="text-charcoal/70 leading-relaxed font-light text-sm md:text-base" data-id="element-46" data-mp="2kw0ghp025v9m">
                        {exp.description}
                      </p>
                    </div>
                  </div>
                </StaggerItem>;
          })}
          </StaggerContainer>
        </div>
      </div>
    </section>;
}